package com.wintermar.inspection.core.ui.theme

import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.sp

/**
 * AppTextStyle
 *
 * Purpose
 * - Centralized set of text tokens that map your Figma design system into Compose.
 * - Exposes readable tokens (Body.tiny, Body.small, etc) that reference styles from `AppTypography`.
 *
 * Behavior
 * - Each property is a `@Composable` getter without a backing field so the returned `TextStyle`
 *   reacts to theme / typography changes at composition time.
 * - Keep `AppTypography` as the single source of truth for sizes, weights and fontFamily.
 *
 * Figma ↔ Compose mapping (examples)
 * - Figma token `body.tiny` (12px) -> `AppTextStyle.Body.tiny` -> `AppTypography.bodySmall`
 * - Figma token `body.small` (14px) -> `AppTextStyle.Body.small` -> `AppTypography.bodyMedium`
 *
 * Guidelines
 * - Prefer using these tokens in UI code instead of inline `TextStyle` literals.
 */
data class AppTextStyle(
    private val appTypography: Typography
) {
    val body: BodyTextStyle = BodyTextStyle(appTypography = appTypography)
    val title: TitleTextStyle = TitleTextStyle(appTypography = appTypography)
}

data class BodyTextStyle(private val appTypography: Typography) {
    val tiny: TextStyle
        @Composable
        get() = appTypography.bodySmall

    val small: TextStyle
        @Composable
        get() = appTypography.bodyMedium

    val regular: TextStyle
        @Composable
        get() = appTypography.bodyLarge

    val large: TextStyle
        @Composable
        get() = appTypography.bodyLarge.copy(fontSize = 18.sp)

    val xl: TextStyle
        @Composable
        get() = appTypography.titleLarge.copy(fontSize = 20.sp)
}

data class TitleTextStyle(private val appTypography: Typography) {
    val s: TextStyle
        @Composable
        get() = appTypography.headlineSmall

    val m: TextStyle
        @Composable
        get() = appTypography.headlineLarge.copy(fontSize = 30.sp)

    val l: TextStyle
        @Composable
        get() = appTypography.displaySmall

    val xl: TextStyle
        @Composable
        get() = appTypography.displayMedium.copy(fontSize = 48.sp)

    val xxl: TextStyle
        @Composable
        get() = appTypography.displayLarge.copy(fontSize = 60.sp)
}
